import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import clsx from 'clsx';
import { ComponentProps, CSSProperties, FocusEvent, Ref, useMemo, useState } from 'react';
import {
  Text as AriaText,
  DateRangePicker as AriaDateRangePicker,
  DateRangePickerProps,
  Label,
  FieldError,
  Group,
  DateInput,
  DateSegment,
  DateSegmentRenderProps,
} from 'react-aria-components';
import IconButton from '~/components/buttons/NewIconButton';
import fieldStyles from '~/components/inputs/Field.module.scss';
import { HStack } from '~/components/layout/Stack';
import CalendarIcon from '~/icons/icon-calendar.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { dateToCalendarDate } from '~/utils/date';
import CalendarRangePopover, { DateRange, PresetDate } from './CalendarRangePopover';
import styles from './DateRangePicker.module.scss';

interface ClearableProps {
  clearable: true;
  onChange: (value: DateRange | null) => void;
}

interface NonClearableProps {
  clearable?: false;
  onChange: (value: DateRange) => void;
}

type Props = {
  className?: string;
  errorMessage?: string;
  shortYear?: boolean;
  helpText?: string;
  hideLabel?: boolean;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  placeholder?: string;
  maxValue?: Date | undefined;
  minValue?: Date | undefined;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  presetDates?: PresetDate[];
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  value: DateRange | null;
  ref?: Ref<HTMLDivElement>;
} & (ClearableProps | NonClearableProps);

const DateRangePicker = ({
  className,
  errorMessage,
  shortYear = true,
  helpText,
  hideLabel,
  id,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  placeholder,
  maxValue,
  minValue,
  onBlur,
  onChange,
  clearable,
  presetDates,
  size = 'medium',
  style,
  value,
  ref,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>();
  const pickerValue = useMemo(
    () => (value ? { start: dateToCalendarDate(value.start), end: dateToCalendarDate(value.end) } : null),
    [value],
  );

  const handleChange: DateRangePickerProps<CalendarDate>['onChange'] = (value): void => {
    if (value) {
      const start = value.start.toDate(getLocalTimeZone());
      const end = value.end.toDate(getLocalTimeZone());
      onChange({ start, end });
    }
  };

  const selectedPreset = presetDates?.find((preset) => {
    return (
      value?.start.getTime() === preset.date.start?.getTime() && value?.end.getTime() === preset.date.end?.getTime()
    );
  });

  const formatSegment = ({ type, text, isFocused }: DateSegmentRenderProps) => {
    if (type === 'year' && shortYear && !isFocused && text.length === 4) {
      return text.slice(-2);
    }

    return text;
  };

  return (
    <AriaDateRangePicker
      ref={ref}
      style={style}
      onBlur={onBlur}
      id={id}
      aria-label={label}
      onOpenChange={setIsOpen}
      isOpen={isOpen}
      data-size={size}
      isDisabled={isDisabled}
      onChange={handleChange}
      isInvalid={isInvalid || !!errorMessage}
      className={clsx(fieldStyles.field, styles.datePickerField, className)}
      minValue={minValue ? dateToCalendarDate(minValue) : undefined}
      maxValue={maxValue ? dateToCalendarDate(maxValue) : undefined}
      value={pickerValue}
    >
      {!hideLabel && (
        <Label>
          {label} {isRequired ? ' *' : null}
        </Label>
      )}

      <Group className={clsx(styles.inputWrapper, { [styles.hasPreset!]: !!selectedPreset })}>
        {placeholder && !value && (
          <span aria-hidden="true" className={clsx(styles.input, styles.placeholder)}>
            {placeholder}
          </span>
        )}
        {selectedPreset && (
          <span aria-hidden="true" className={clsx(styles.input, styles.presetDate)}>
            {selectedPreset.label}
          </span>
        )}
        <HStack align="center" className={styles.dateInputs}>
          <DateInput className={styles.input} slot="start">
            {(segment) => (
              <DateSegment className={styles.segment} segment={segment}>
                {formatSegment}
              </DateSegment>
            )}
          </DateInput>
          <span className={styles.segment} aria-hidden="true">
            {'–'}
          </span>
          <DateInput className={styles.input} slot="end">
            {(segment) => (
              <DateSegment className={styles.segment} segment={segment}>
                {formatSegment}
              </DateSegment>
            )}
          </DateInput>
        </HStack>
        <HStack>
          {!isDisabled && clearable && value && (
            <ClearButton onPress={() => onChange(null)} size={size === 'large' ? 'medium' : 'small'} />
          )}
          <IconButton
            className={styles.calendarButton}
            iconClassName={styles.icon}
            Icon={CalendarIcon}
            disabled={isDisabled}
            variant="ghost"
            size={size === 'large' ? 'medium' : 'small'}
          />
        </HStack>
      </Group>

      {helpText && <AriaText slot="description">{helpText}</AriaText>}
      <FieldError>{errorMessage}</FieldError>
      <CalendarRangePopover
        presetDates={presetDates}
        onSelectDate={({ start, end }) => {
          if (!start || !end) return;
          onChange({ start, end });
          setIsOpen(false);
        }}
      />
    </AriaDateRangePicker>
  );
};

const ClearButton = ({ onPress, size }: { onPress: () => void; size: ComponentProps<typeof IconButton>['size'] }) => {
  return (
    <IconButton
      className={styles.clear}
      label="clear"
      iconClassName={styles.icon}
      Icon={IconClose}
      variant="ghost"
      size={size}
      onPressStart={onPress}
    />
  );
};

export default DateRangePicker;
